import { Navigate, useNavigate } from '@tanstack/react-router'
import { AlertCircle, Loader2 } from 'lucide-react'
import { FormEvent, useState } from 'react'
import styles from './Index.module.css'
import { AuthSplitLayout } from '@/components/auth/AuthSplitLayout'
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { useAuth } from '@/contexts/AuthContext'
import { getHomeRoute } from '@/lib/routes'

export default function Index() {
  const {
    isAuthenticated,
    profile,
    loginWithRedirect,
    loginWithPassword,
    loading: authLoading,
    isLoggingOut,
    authError,
    logout,
  } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  if (authLoading || isLoggingOut) {
    return (
      <div className="flex justify-center items-center h-dvh" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (isAuthenticated) {
    if (profile) {
      return <Navigate to={getHomeRoute(profile.role)} replace />
    }

    return (
      <RouteErrorBoundary
        error={new Error(authError || 'We could not finish signing you in. Sign out and try again.')}
        title="Session issue"
        retryLabel="Sign Out"
        onRetry={() => void logout().catch(() => {})}
      />
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)

    setIsSubmitting(true)
    const { error } = await loginWithPassword(email, password)

    if (error) {
      setFormError(error.message)
      setIsSubmitting(false)
      return
    }

    setIsSubmitting(false)
  }

  return (
    <AuthSplitLayout
      heading="Welcome Back"
      subtitle="Enter your details to log in to your Doneverse Dashboard"
      onGoogleClick={() =>
        loginWithRedirect({
          authorizationParams: { connection: 'google-oauth2' },
        })
      }
    >
      <>
        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={styles.input}
            placeholder="Email ID"
            aria-label="Email ID"
            disabled={isSubmitting}
            required
          />

          <input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className={styles.input}
            placeholder="Password"
            aria-label="Password"
            disabled={isSubmitting}
            required
          />

          <button
            type="button"
            onClick={() => navigate({ to: '/forgot-password' })}
            className={styles.forgotPasswordButton}
            disabled={isSubmitting}
          >
            Forgot Your Password?
          </button>

          <button
            className={`${styles.loginButton} ${styles.loginSubmitButton}`}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Logging In...' : 'Log In'}
          </button>
        </form>

        {formError && (
          <div className={styles.errorCard} role="alert">
            <div className={styles.errorTitle}>
              <AlertCircle className="h-4 w-4" />
              <span>Login Error</span>
            </div>
            <p className={styles.errorMessage}>{formError}</p>
          </div>
        )}
      </>
    </AuthSplitLayout>
  )
}
